import React from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faBoxesStacked,
    faBuilding,
    faPrint,
    faServer,
    faTableCellsLarge,
    faLock,
    faArrowsRotate,
    faTowerBroadcast,
    faPowerOff,
    faCashRegister,
    faTags,
    faChartColumn,
    faClockRotateLeft,
    faUserClock,
    faReceipt,
    faPercent,
    faFileInvoiceDollar,
    faHeading,
    faClock,
    faStar,
    faThumbtack,
    faMoneyBillWave,
    faBell,
    faKitchenSet,
    faBagShopping,
    faClipboardList,
    faCreditCard,
    faBolt,
    faGauge,
    faCloudArrowUp,
    faStore,
    faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { HappyHourView } from '../../../../services/happy-hour/happy-hour.service';
import { ChefHatIcon } from './ChefHatIcon';
import classes from './settings-hub-modal.module.scss';

export type SettingsHubKey =
    | 'employees'
    | 'products'
    | 'categories'
    | 'printers'
    | 'agents'
    | 'session'
    | 'areasEdit'
    | 'backoffice'
    | 'refresh'
    | 'refreshAll'
    | 'kds'
    | 'openDrawer'
    | 'openingCash'
    | 'reportX'
    | 'restoreZ'
    | 'workerReport'
    | 'zcreditTransactions'
    | 'testPush'
    | 'pastReceipts'
    | 'vatRate'
    | 'headerEdit'
    | 'accountantReport'
    | 'autoCloseSession'
    | 'happyHour'
    | 'takeAway'
    | 'dailyBrief'
    | 'quickCharge'
    | 'kioskMode'
    | 'minOrder'
    | 'restigo'
    | 'posAssistant'
    | 'posSettings'
    | 'fixedPrinter';

export type SettingsHubTile = {
    key: SettingsHubKey;
    label: string;
    icon: IconProp;
    tone:
        | 'mint'
        | 'mintgreen'
        | 'green'
        | 'orange'
        | 'pumpkin'
        | 'lilac'
        | 'gold'
        | 'sky'
        | 'cyan'
        | 'aqua'
        | 'lime'
        | 'peach'
        | 'periwinkle'
        | 'skyblue'
        | 'pink'
        | 'salmon'
        | 'butter'
        | 'sand'
        | 'rose'
        | 'danger';
    iconDanger?: boolean;
    subtitle?: string;
    active?: boolean;
};

export type SettingsHubGroup = {
    key: string;
    title: string;
    tiles: SettingsHubTile[];
};

export type SettingsHubModalProps = {
    opened: boolean;
    onClose: () => void;
    onSelectTile: (key: SettingsHubKey) => void;
    isManager: boolean;
    isSuperAdmin: boolean;
    employeeName: string;
    employeePhotoUrl?: string | null;
    onSwitchEmployee: () => void;
    autoCloseTime?: string | null;
    happyHour?: HappyHourView | null;
    fixedPrinterName?: string | null;
    restigoEnabled?: boolean;
    kioskModeActive?: boolean;
};

// Summary: Tile-grid hub modal opened by the POS top-bar hamburger button.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-settingshubmodal
// either opens a dedicated sub-modal (employees / products / printers / agents
// / session) or navigates the shell into Areas edit mode. Tiles are rendered in
// labelled groups mirroring the manager operations dashboard reference.
// Touches:
//   - PosShellPage.tsx (owns settings hub state + sub-modal state, passes
//     onSelectTile that maps a tile key to the right action).
//   - PosTopBar.tsx (its hamburger button toggles `opened`).
// Breaking change risk: Renaming a SettingsHubKey requires updating
// PosShellPage's switch in onSelectTile or the matching sub-modal will not open.
const SettingsHubModalComponent: React.FC<SettingsHubModalProps> = ({
    opened,
    onClose,
    onSelectTile,
    isManager,
    isSuperAdmin,
    employeeName,
    employeePhotoUrl,
    onSwitchEmployee,
    autoCloseTime,
    happyHour = null,
    fixedPrinterName,
    restigoEnabled = false,
    kioskModeActive = false,
}) => {
    // Phone-only: TA + daily-brief live here since the area-bar hides them below tablet width.
    const isPhone = useMediaQuery('(max-width: 767px)');
    // areasEdit is a desktop-web-only tile — hidden on phone and tablet/iPad widths.
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const initial = employeeName?.trim().charAt(0).toUpperCase() || '?';
    const handleSwitch = () => {
        onClose();
        onSwitchEmployee();
    };
    const groups: SettingsHubGroup[] = [
        {
            key: 'daily',
            title: 'פעולות יומיות',
            tiles: [
                ...(isManager
                    ? ([{ key: 'posAssistant', label: 'עוזר קופה', icon: faWandMagicSparkles as IconProp, tone: 'lilac' }] as SettingsHubTile[])
                    : []),
                { key: 'refresh', label: 'רענן', icon: faArrowsRotate as IconProp, tone: 'cyan' },
                { key: 'backoffice', label: 'בקאופיס', icon: faBuilding as IconProp, tone: 'skyblue' },
                ...(isPhone
                    ? ([
                          { key: 'takeAway', label: 'לקחת', icon: faBagShopping as IconProp, tone: 'green' },
                          { key: 'dailyBrief', label: 'בריף יומי', icon: faClipboardList as IconProp, tone: 'sky' },
                      ] as SettingsHubTile[])
                    : []),
                ...(isManager
                    ? ([
                          { key: 'openDrawer', label: 'פתח מגירה', icon: faCashRegister as IconProp, tone: 'mint' },
                          { key: 'refreshAll', label: 'רענן את כל המכשירים', icon: faTowerBroadcast as IconProp, tone: 'sky' },
                          { key: 'quickCharge', label: 'חיוב מהיר', icon: faBolt as IconProp, tone: 'orange' },
                          { key: 'kds', label: 'מסך מטבח', icon: faKitchenSet as IconProp, tone: 'mint' },
                          { key: 'happyHour', label: 'Happy Hour', icon: faStar as IconProp, tone: 'pumpkin' },
                          { key: 'session', label: 'Z סוף יום', icon: faPowerOff as IconProp, tone: 'green', iconDanger: true },
                      ] as SettingsHubTile[])
                    : []),
            ],
        },
        ...(isManager
            ? ([
                  {
                      key: 'reports',
                      title: 'דוחות',
                      tiles: [
                          { key: 'reportX', label: 'דוח X', icon: faChartColumn as IconProp, tone: 'peach' },
                          { key: 'restoreZ', label: 'שחזר דוח Z', icon: faClockRotateLeft as IconProp, tone: 'lilac' },
                          { key: 'workerReport', label: 'דוח עובד', icon: faUserClock as IconProp, tone: 'mint' },
                          { key: 'zcreditTransactions', label: 'עסקאות אשראי', icon: faCreditCard as IconProp, tone: 'sky' },
                          { key: 'accountantReport', label: 'דו"ח רואה חשבון', icon: faFileInvoiceDollar as IconProp, tone: 'gold' },
                      ],
                  },
                  {
                      key: 'management',
                      title: 'ניהול',
                      tiles: [
                          ...(isDesktop
                              ? ([{ key: 'areasEdit', label: 'עריכת אזורים', icon: faTableCellsLarge as IconProp, tone: 'peach' }] as SettingsHubTile[])
                              : []),
                          { key: 'pastReceipts', label: 'קבלות עבר', icon: faReceipt as IconProp, tone: 'periwinkle' },
                          { key: 'employees', label: 'ניהול עובדים', icon: faUsers as IconProp, tone: 'cyan' },
                          { key: 'products', label: 'ניהול מוצרים', icon: faBoxesStacked as IconProp, tone: 'skyblue' },
                          { key: 'categories', label: 'ניהול קטגוריות', icon: faTags as IconProp, tone: 'butter' },
                          {
                              key: 'autoCloseSession',
                              label: 'סגירה אוטומטית',
                              icon: faClock as IconProp,
                              tone: 'salmon',
                              subtitle: autoCloseTime ?? 'כבוי',
                              active: autoCloseTime !== null,
                          },
                          { key: 'openingCash', label: 'מזומן פתיחה', icon: faMoneyBillWave as IconProp, tone: 'mintgreen' },
                          { key: 'minOrder', label: 'מינימום הזמנה', icon: faGauge as IconProp, tone: 'orange' },
                          {
                              key: 'kioskMode',
                              label: 'מצב קיוסק',
                              icon: faStore as IconProp,
                              tone: 'green',
                              subtitle: kioskModeActive ? 'פעיל' : 'כבוי',
                              active: kioskModeActive,
                          },
                          {
                              key: 'fixedPrinter',
                              label: 'קיבוע מדפסת',
                              icon: faThumbtack as IconProp,
                              tone: 'gold',
                              subtitle: fixedPrinterName ?? undefined,
                              active: Boolean(fixedPrinterName),
                          },
                          // Keyless tenant => restigoEnabled false => tile absent (zero surface).
                          ...(restigoEnabled
                              ? ([{ key: 'restigo', label: 'רסטיגו', icon: faCloudArrowUp as IconProp, tone: 'skyblue' }] as SettingsHubTile[])
                              : []),
                          { key: 'posSettings', label: 'הגדרות POS', icon: faLock as IconProp, tone: 'lilac' },
                      ],
                  },
              ] as SettingsHubGroup[])
            : []),
        ...(isSuperAdmin && isManager
            ? ([
                  {
                      key: 'superAdmin',
                      title: 'ניהול-על',
                      tiles: [
                          { key: 'agents', label: 'סוכני מדפסת', icon: faServer as IconProp, tone: 'pink' },
                          { key: 'printers', label: 'ניהול הדפסות', icon: faPrint as IconProp, tone: 'orange' },
                          { key: 'testPush', label: 'בדיקת פוש', icon: faBell as IconProp, tone: 'sky' },
                          { key: 'vatRate', label: 'עדכון מע"מ', icon: faPercent as IconProp, tone: 'butter' },
                          { key: 'headerEdit', label: 'עדכון כותרת', icon: faHeading as IconProp, tone: 'sand' },
                      ],
                  },
              ] as SettingsHubGroup[])
            : []),
    ];

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            onContextMenu={(event) => event.preventDefault()}
            title={<span className={classes.title}>פעולות ניהול</span>}
            size="lg"
            centered
            radius="md"
            zIndex={301}
            overlayProps={{ opacity: 0.55, blur: 2 }}
            closeButtonProps={{ 'data-testid': 'settings-hub-close' } as Record<string, string>}
        >
            <header className={classes.cashierCard}>
                <span className={classes.cashierAvatar}>
                    {employeePhotoUrl ? (
                        <img src={employeePhotoUrl} alt={employeeName} />
                    ) : (
                        initial
                    )}
                </span>
                <span className={classes.cashierName}>{employeeName}</span>
                <UnstyledButton
                    type="button"
                    data-testid="settings-switch-employee"
                    className={classes.cashierLockButton}
                    onClick={handleSwitch}
                    aria-label="החלף עובד"
                    title="החלף עובד"
                >
                    <FontAwesomeIcon icon={faLock as IconProp} />
                    <span>החלף עובד</span>
                </UnstyledButton>
            </header>

            {groups.map((group) => (
                <section key={group.key} className={classes.group}>
                    <header className={classes.groupHeader}>
                        <span className={classes.groupDot} />
                        <h4 className={classes.groupTitle}>{group.title}</h4>
                    </header>
                    <div className={classes.grid}>
                        {group.tiles.map((tile) => (
                            tile.key === 'happyHour' ? (
                                <UnstyledButton
                                    key={tile.key}
                                    type="button"
                                    data-testid={`settings-tile-${tile.key}`}
                                    className={`${classes.tile} ${classes[`tone_${tile.tone}`]} ${happyHour?.active ? classes.tileLive : ''}`}
                                    onClick={() => onSelectTile(tile.key)}
                                >
                                    <span
                                        className={`${classes.tileIcon} ${tile.iconDanger ? classes.tileIconDanger : ''}`}
                                    >
                                        <FontAwesomeIcon icon={tile.icon} />
                                    </span>
                                    <span className={classes.tileLabel}>
                                        {happyHour?.active ? (
                                            <>
                                                Happy Hour
                                                <br />
                                                ✓ פעיל
                                            </>
                                        ) : tile.label}
                                    </span>
                                </UnstyledButton>
                            ) : (
                            <UnstyledButton
                                key={tile.key}
                                type="button"
                                data-testid={`settings-tile-${tile.key}`}
                                data-active={tile.active ? 'true' : undefined}
                                className={`${classes.tile} ${classes[`tone_${tile.tone}`]} ${tile.iconDanger ? classes.tileDanger : ''} ${tile.active ? classes.tileActive : ''}`}
                                onClick={() => onSelectTile(tile.key)}
                            >
                                {tile.subtitle && <span className={classes.tileSubtitleCorner}>{tile.subtitle}</span>}
                                <span
                                    className={`${classes.tileIcon} ${tile.iconDanger ? classes.tileIconDanger : ''}`}
                                >
                                    {tile.key === 'kds' ? <ChefHatIcon className={classes.chefHatIcon} /> : <FontAwesomeIcon icon={tile.icon} />}
                                </span>
                                <span className={classes.tileLabel}>{tile.label}</span>
                            </UnstyledButton>
                            )
                        ))}
                    </div>
                </section>
            ))}
        </Modal>
    );
};

export const SettingsHubModal = React.memo(SettingsHubModalComponent);
